import React, { useState, useEffect, useRef } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import { DeploymentPlayer } from 'cccisd-laravel-assignment';
import IconFamily from 'cccisd-icons/users4';
import IconQuestion from 'cccisd-icons/question4';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import Tabs from 'cccisd-tabs';
import Tooltip from 'cccisd-tooltip';
import { TFH_DEPLOYMENT_HANDLE } from '../../TfhProgram';
import query from './data.graphql';
import style from './style.css';

const FAMILY_PROFILE_DEPLOYMENT_HANDLE = 'tfh_family_profile';
const initialTab = 'overview';

const FamilyProfile = ({ family, familyName, isStartOpen }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const modalRef = useRef(null);

    const pawnId = _get(family, 'descendantRoles.metricspawn.pawn.pawnId');
    const pawnHash = _get(family, 'descendantRoles.metricspawn.pawn.pawnHash');

    useEffect(() => {
        if (isStartOpen && modalRef.current) {
            modalRef.current.open();
        }
    }, [isStartOpen, modalRef]);

    async function getData() {
        setIsLoading(true);
        const resp = await client.query({
            query,
            variables: {
                pawnId,
                pawnHash,
                courseDeploymentHandle: TFH_DEPLOYMENT_HANDLE,
                profileDeploymentHandle: FAMILY_PROFILE_DEPLOYMENT_HANDLE,
            },
            fetchPolicy: 'network-only',
        });

        const respData = _get(resp, 'data.roles.metricspawn');
        setData(respData);
        setIsLoading(false);
    }

    function renderNoData() {
        return (
            <div className="alert alert-dark">
                <p className="mb-0">No data to show at this time</p>
            </div>
        );
    }

    function renderSex(key) {
        let selected = data.course.devTags[`demographics${key}Sex`];
        if (selected) {
            return selected;
        }

        let userDefined = data.course.devTags[`demographics${key}SexOther`];
        if (userDefined) {
            return userDefined;
        }

        return '-';
    }

    function renderRace(key) {
        let combineFormula = data.course.devTags[`demographics${key}Race`];
        const list = combineFormula
            .replace(/-/g, '')
            .split(',')
            .filter(z => z);
        if (!list.length) {
            return '-';
        }

        return list.join(', ');
    }

    function renderDemographicsRow(key) {
        if (!['Parent', 'Child1', 'Child2', 'Child3', 'Child4'].includes(key)) {
            return;
        }

        if (!data?.course?.devTags) {
            return key === 'Parent' ? renderNoData() : <span />;
        }

        const demographicsDevTags = data.course.devTags;

        // if no data at all, don't bother showing this row. It was probably skipped with visibility logic
        if (
            !Object.keys(demographicsDevTags).some(devTag => {
                return devTag.includes(key) && !!demographicsDevTags[devTag];
            })
        ) {
            return key === 'Parent' ? renderNoData() : <span />;
        }

        return (
            <div className={'row ' + style.demographicsRow}>
                <div className="col-12 col-sm-1">{key.replace(/[\d]/g, '')}</div>
                <div className="col-12 col-sm-11">
                    <dl className="row mb-0">
                        <dt className="col-4 my-0 py-0 text-end">Birthdate:</dt>
                        <dd className="col-8 my-0 py-0 text-start">
                            {demographicsDevTags[`demographics${key}Birthdate`] || '-'}
                        </dd>
                        <dt className="col-4 my-0 py-0 text-end">Sex:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{renderSex(key)}</dd>
                        <dt className="col-4 my-0 py-0 text-end">Hispanic:</dt>
                        <dd className="col-8 my-0 py-0 text-start">
                            {demographicsDevTags[`demographics${key}Hispanic`] || '-'}
                        </dd>
                        <dt className="col-4 my-0 py-0 text-end">Race:</dt>
                        <dd className="col-8 my-0 py-0 text-start">{renderRace(key)}</dd>
                        <dt className="col-4 my-0 py-0 text-end">Education:</dt>
                        <dd className="col-8 my-0 py-0 text-start">
                            {demographicsDevTags[`demographics${key}Education`] || '-'}
                        </dd>
                        {key === 'Parent' && (
                            <>
                                <dt className="col-4 my-0 py-0 text-end">Work Status:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}WorkStatus`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Relationship Status:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}Relationship`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Household Members:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}HouseholdMemberCount`] || '-'}
                                </dd>
                                <dt className="col-4 my-0 py-0 text-end">Household Income:</dt>
                                <dd className="col-8 my-0 py-0 text-start">
                                    {demographicsDevTags[`demographics${key}HouseholdIncome`] || '-'}
                                </dd>
                            </>
                        )}
                    </dl>
                </div>
            </div>
        );
    }

    return (
        <Modal
            ref={modalRef}
            title={'Family Profile - ' + familyName}
            size="large"
            beforeShow={() => getData()}
            trigger={
                <button type="button" className="btn btn-sm btn-secondary">
                    <IconFamily spaceRight />
                    Profile
                </button>
            }
            render={({ closeModal }) => {
                if (isLoading || !data) {
                    return (
                        <div className="py-5">
                            <Loader loading type="inline" />;
                        </div>
                    );
                }

                const tabList = [
                    {
                        name: 'overview',
                        title: 'Overview',
                        content: (
                            <div className={style.modalContent}>
                                <div>
                                    <div className="mb-5 mt-1">
                                        <h5>
                                            Demographics
                                            <Tooltip
                                                title={
                                                    <span>
                                                        Demographics information is entered by the family at the start
                                                        of the program. It is not editable.
                                                    </span>
                                                }
                                            >
                                                <span className="fs-6 text-primary">
                                                    <IconQuestion spaceLeft />
                                                </span>
                                            </Tooltip>
                                        </h5>
                                        {renderDemographicsRow('Parent')}
                                        {renderDemographicsRow('Child1')}
                                        {renderDemographicsRow('Child2')}
                                        {renderDemographicsRow('Child3')}
                                    </div>
                                    <div className="mb-5 mt-1">
                                        <h5>
                                            Profile
                                            <Tooltip
                                                title={
                                                    <span>
                                                        Profile information is editable by clicking the{' '}
                                                        <b>Update Profile</b> tab.
                                                    </span>
                                                }
                                            >
                                                <span className="fs-6 text-primary">
                                                    <IconQuestion spaceLeft />
                                                </span>
                                            </Tooltip>
                                        </h5>
                                        <p className="text-body-secondary fst-italic fw-light">
                                            Note: If this table does not appear to be updating, you might need to click
                                            &quot;Save&quot; in the Update Profile tab.
                                        </p>
                                        TABLE
                                    </div>
                                </div>
                                <div>
                                    <button className="btn btn-secondary" onClick={() => closeModal()} type="button">
                                        Close
                                    </button>
                                </div>
                            </div>
                        ),
                    },
                    {
                        name: 'Update Profile',
                        title: 'Update Profile',
                        content: (
                            <div className={style.modalContent} data-cccisd-survey-area>
                                <DeploymentPlayer
                                    pawnId={pawnId}
                                    pawnHash={pawnHash}
                                    disableLayout
                                    deploymentHandle={FAMILY_PROFILE_DEPLOYMENT_HANDLE}
                                />
                            </div>
                        ),
                    },
                ];

                return <Tabs tabList={tabList} initialTab={initialTab} />;
            }}
        />
    );
};

export default FamilyProfile;
